import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Star, Award, Flame, Zap, Briefcase, MapPin, Wrench, Factory, ArrowLeft, Trophy, Send, Check, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const badgeConfig = {
  GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
  SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30' },
  BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30' },
};

const StatRow = ({ label, icon: Icon, values, highlight }) => {
  const best = highlight ? Math.max(...values.map(v => (typeof v === 'number' ? v : 0))) : null;
  return (
    <tr className="border-b border-zinc-800/60">
      <td className="py-3 px-4 text-sm text-zinc-400 flex items-center gap-2 whitespace-nowrap">
        <Icon className="w-4 h-4 text-zinc-500" />{label}
      </td>
      {values.map((val, i) => (
        <td key={i} className={`py-3 px-4 text-center text-sm ${highlight && typeof val === 'number' && val === best && val > 0 ? 'text-brand-gold font-semibold' : 'text-brand-text'}`}>
          {val}
        </td>
      ))}
    </tr>
  );
};

const TagRow = ({ label, icon: Icon, lists }) => (
  <tr className="border-b border-zinc-800/60">
    <td className="py-3 px-4 text-sm text-zinc-400 flex items-center gap-2 whitespace-nowrap">
      <Icon className="w-4 h-4 text-zinc-500" />{label}
    </td>
    {lists.map((tags, i) => (
      <td key={i} className="py-3 px-4 text-center">
        {tags && tags.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-center">
            {tags.map((t, j) => (
              <span key={j} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                {t}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        )}
      </td>
    ))}
  </tr>
);

const InviteCell = ({ spId, spName, posts, token }) => {
  const [open, setOpen] = useState(false);
  const [invited, setInvited] = useState({});
  const [sending, setSending] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleInvite = async (postId, postTitle) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/business/invite/${spId}?post_id=${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send invite');
      }
      toast.success(`Invited ${spName} to "${postTitle}"`);
      setInvited(prev => ({ ...prev, [postId]: true }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  if (!posts || posts.length === 0) {
    return <span className="text-xs text-zinc-600">No open posts</span>;
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`invite-btn-${spId}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 transition-colors"
      >
        <Send className="w-3 h-3" />
        Invite to Sell
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          data-testid={`invite-posts-${spId}`}
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-64 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-20 overflow-hidden"
        >
          <div className="px-3 py-2 text-[10px] text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
            Select a post
          </div>
          {posts.map((post) => (
            <button
              key={post.id}
              disabled={sending || invited[post.id]}
              onClick={() => handleInvite(post.id, post.title)}
              data-testid={`invite-post-${post.id}-${spId}`}
              className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 transition-colors ${
                invited[post.id]
                  ? 'text-green-400 bg-green-500/5'
                  : 'text-zinc-300 hover:bg-zinc-800'
              }`}
            >
              <span className="truncate">{post.title}</span>
              {invited[post.id] ? (
                <Check className="w-3 h-3 flex-shrink-0 text-green-400" />
              ) : (
                <Send className="w-3 h-3 flex-shrink-0 text-zinc-500" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CompareSalespeople() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];

  useEffect(() => {
    if (!token || ids.length < 2) {
      setError('Select at least 2 salespeople to compare.');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const [compareRes, postsRes] = await Promise.all([
          axios.post(`${API}/business/compare`, { ids }, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/business/posts`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setProfiles(compareRes.data);
        setPosts(postsRes.data.filter(p => p.status !== 'CLOSED'));
      } catch {
        setError('Failed to load comparison data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, ids.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || profiles.length < 2) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-400">{error || 'Not enough profiles to compare.'}</p>
        <Button onClick={() => navigate('/business/dashboard')} variant="outline" className="border-brand-gold text-brand-gold">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  const colCount = profiles.length;

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text" data-testid="compare-view">
      <Toaster position="top-center" />
      <header className="bg-brand-surface/80 backdrop-blur border-b border-zinc-800 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/business/dashboard')}
              className="text-zinc-400 hover:text-brand-text"
              data-testid="compare-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="font-display text-lg text-brand-text tracking-wide">Compare Salespeople</h1>
            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-gold/10 text-brand-gold">{colCount} selected</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="w-40 p-4" />
                {profiles.map((p) => (
                  <th key={p.id} className="p-4 text-center" data-testid={`compare-col-${p.id}`}>
                    <div className="flex flex-col items-center gap-2">
                      {p.avatar ? (
                        <img src={p.avatar} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2 border-zinc-700" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold text-xl font-bold border-2 border-brand-gold/30">
                          {p.name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-semibold text-brand-text">{p.name}</span>
                      {p.reputation?.badge && badgeConfig[p.reputation.badge] && (
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${badgeConfig[p.reputation.badge].color}`}>
                          <Award className="w-3 h-3" />
                          {badgeConfig[p.reputation.badge].label}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <StatRow label="Rating" icon={Star} values={profiles.map(p => p.reputation?.avgStars?.toFixed(1) ?? '0.0')} highlight={false} />
              <StatRow label="Reviews" icon={Star} values={profiles.map(p => p.reputation?.reviewsCount ?? 0)} highlight={true} />
              <StatRow label="XP Points" icon={Zap} values={profiles.map(p => p.xp ?? 0)} highlight={true} />
              <StatRow label="Deals Won" icon={Briefcase} values={profiles.map(p => p.completedDeals ?? 0)} highlight={true} />
              <StatRow label="Streak" icon={Flame} values={profiles.map(p => p.streak ? `${p.streak} mo` : '0')} highlight={false} />
              <tr className="border-b border-zinc-800/60">
                <td className="py-3 px-4 text-sm text-zinc-400 flex items-center gap-2 whitespace-nowrap">
                  <Trophy className="w-4 h-4 text-zinc-500" />Badge
                </td>
                {profiles.map((p, i) => {
                  const b = p.reputation?.badge;
                  const cfg = b ? badgeConfig[b] : null;
                  return (
                    <td key={i} className="py-3 px-4 text-center">
                      {cfg ? (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${cfg.color}`}>
                          <Award className="w-3 h-3" />{cfg.label}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-600">None</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              <TagRow label="Skills" icon={Wrench} lists={profiles.map(p => p.skills)} />
              <TagRow label="Industries" icon={Factory} lists={profiles.map(p => p.industries)} />
              <TagRow label="Regions" icon={MapPin} lists={profiles.map(p => p.regions)} />
              {/* Invite Row */}
              <tr>
                <td className="py-4 px-4 text-sm text-zinc-400 flex items-center gap-2 whitespace-nowrap">
                  <Send className="w-4 h-4 text-zinc-500" />Action
                </td>
                {profiles.map((p) => (
                  <td key={p.id} className="py-4 px-4 text-center">
                    <InviteCell spId={p.id} spName={p.name} posts={posts} token={token} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
